import React from "react";
import { MOCK } from "./data.jsx";
import { Ico, Spark, StatusPill } from "./primitives.jsx";
import { useResource, useMe, apiPost, apiPatch, apiDelete, navigate } from "./api.jsx";
import { setAppearance, currentAppearance } from "./nav.jsx";

function exportUsersCsv(rows) {
  if (!rows || rows.length === 0) return;
  const cols = ["id", "email", "name", "company_name", "account_type", "is_admin_approved", "is_investor_approved", "is_suspended", "is_email_verified", "joined"];
  const header = cols.join(",");
  const body = rows.map(r => cols.map(c => {
    const v = r[c];
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `venzyme-users-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function AdminOverview() {
  const analytics = useResource("/admin/analytics/");
  const users = useResource("/admin/users/?filter=active");
  const kpis = analytics.data?.kpis || {};
  const modalities = analytics.data?.modalities || [];
  const maxCount = modalities.reduce((m, r) => Math.max(m, r.count || 0), 1);
  const recent = (users.data?.results || []).slice(0, 4);

  return (
    <div className="page route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>Administration · Overview</div>
          <h1 className="page-title">System pulse.</h1>
        </div>
        <div className="pill pill--good"><i className="dot"/>All systems nominal</div>
      </div>
      <div className="grid grid--4">
        <KPICard label="Users" value={(kpis.users_total ?? 0).toLocaleString()}/>
        <KPICard label="Active" value={(kpis.users_active ?? 0).toLocaleString()}/>
        <KPICard label="Analyses · 30d" value={(kpis.decks_30d ?? 0).toLocaleString()}/>
        <KPICard label="Analyses · YTD" value={(kpis.decks_ytd ?? 0).toLocaleString()}/>
      </div>

      <div className="grid" style={{gridTemplateColumns:"1fr 1fr", gap:24, marginTop:28, alignItems:"start"}}>
        <div className="card card--flush">
          <div className="card-header">
            <div><div className="eyebrow">Modality distribution</div><div className="title">All analyses</div></div>
          </div>
          <div className="card-body">
            {modalities.length === 0 && !analytics.loading && (
              <div className="dim" style={{textAlign:"center", padding:20}}>No data yet.</div>
            )}
            {modalities.map(({ name, count })=>{
              const pct = (count / maxCount) * 100;
              return (
                <div key={name} style={{marginBottom:12}}>
                  <div className="row" style={{fontSize:13, marginBottom:6}}>
                    <span>{name}</span><div className="spacer"/><span className="num dim">{count}</span>
                  </div>
                  <div className="bar"><i style={{width:pct+"%"}}/></div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="card card--flush">
          <div className="card-header">
            <div><div className="eyebrow">Recent users</div><div className="title">Active accounts</div></div>
            <button className="btn btn--quiet" onClick={() => navigate("admin-users")}>All users →</button>
          </div>
          <table className="t">
            <tbody>
              {recent.length === 0 && !users.loading && (
                <tr><td colSpan={3} className="dim" style={{textAlign:"center", padding:20}}>No active users.</td></tr>
              )}
              {recent.map(u=>(
                <tr key={u.id}>
                  <td><div style={{fontSize:13}}>{u.name}</div><div className="dim" style={{fontSize:12}}>{u.email}</div></td>
                  <td className="dim" style={{fontSize:13}}>{u.company_name || "—"}</td>
                  <td><StatusPill status={u.is_suspended ? "pass" : (u.is_admin_approved || u.is_investor_approved || u.account_type === "company" ? "shortlist" : "review")}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function KPICard({ label, value, delta, bad }) {
  return (
    <div className="card">
      <div className="h-eyebrow">{label}</div>
      <div style={{display:"flex", alignItems:"baseline", gap:10, marginTop:6}}>
        <div className="big-num">{value}</div>
        {delta && <span className={"pill " + (bad ? "pill--bad" : "pill--good")} style={{fontSize:11}}>{delta}</span>}
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [filter, setFilter] = React.useState("active");
  const [search, setSearch] = React.useState("");
  const params = new URLSearchParams();
  if (filter) params.set("filter", filter);
  if (search) params.set("search", search);
  const qs = params.toString() ? `?${params.toString()}` : "";

  const users = useResource(`/admin/users/${qs}`);
  const rows = users.data?.results || [];
  const me = useMe();
  const meIsSuperuser = !!me.data?.is_admin;

  const callAction = async (user, verb) => {
    try {
      const body = verb === "suspend" ? { reason: "Suspended from admin console" } : undefined;
      await apiPost(`/admin/users/${user.id}/${verb}/`, body || {});
      users.reload();
    } catch (err) {
      alert(`Action failed: ${err.message || "unknown error"}`);
    }
  };

  const startViewAs = async (user) => {
    if (!confirm(`View the app as ${user.name || user.email}? You'll see exactly what they see. Use the banner at the top to return to your admin account.`)) return;
    try {
      await apiPost("/admin/view-as/", { user_id: user.id });
      // Clear any stored SPA route from the admin session — otherwise the
      // impersonated non-admin would land on admin-* and 403 on data load.
      try {
        localStorage.removeItem("vz-route");
        localStorage.removeItem("vz-analysis-id");
        localStorage.removeItem("vz-submission-id");
      } catch { /* ignore */ }
      // Hard-reload so all caches (useMe, useResource hooks, sidebar) refetch
      // under the new effective user. landing_spa picks up at "dashboard".
      location.href = "/";
    } catch (err) {
      alert(`Couldn't start view-as: ${err.body?.error || err.message || "unknown error"}`);
    }
  };

  const statusOf = (u) => {
    if (u.is_suspended) return "pass";
    if (u.account_type === "administrator" && !u.is_admin_approved) return "review";
    if (u.account_type === "investor" && !u.is_investor_approved) return "review";
    return "shortlist";
  };

  return (
    <div className="page route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>Administration · Users</div>
          <h1 className="page-title">Everyone with access.</h1>
        </div>
        <div className="row" style={{gap:10}}>
          <button
            className="btn btn--ghost"
            onClick={() => exportUsersCsv(rows)}
            disabled={rows.length === 0}
            title="Download the current user list as CSV"
          >Export CSV</button>
          <button
            className="btn btn--primary"
            onClick={() => { location.href = "/admin-dashboard/"; }}
            title="Open the full admin dashboard to invite users and manage approvals"
          ><Ico n="plus" size={14}/> Invite user</button>
        </div>
      </div>

      <div className="row" style={{marginBottom:16, gap:10}}>
        <div className="search" style={{minWidth:320, padding:0, background:"transparent", border:"none"}}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name, email, company…"
            style={{
              width:"100%", padding:"8px 12px",
              border:"1px solid var(--line)", borderRadius:3,
              background:"var(--surface)", fontSize:13, color:"var(--text)", outline:"none",
            }}
          />
        </div>
        <div className="seg">
          {[
            ["", "All"], ["active", "Active"], ["pending", "Pending"], ["staff", "Staff"], ["suspended", "Suspended"],
          ].map(([v, l]) => (
            <button key={v} className={filter === v ? "on" : ""} onClick={() => setFilter(v)}>{l}</button>
          ))}
        </div>
        <div className="spacer"/>
        <div className="dim" style={{fontSize:13}}>{users.data?.count ?? "…"} users</div>
      </div>

      <div className="card card--flush" style={{overflowX:"auto"}}>
        <table className="t" style={{minWidth:880, tableLayout:"fixed", width:"100%"}}>
          <colgroup>
            <col style={{width:"18%"}}/>
            <col style={{width:"22%"}}/>
            <col style={{width:"16%"}}/>
            <col style={{width:"12%"}}/>
            <col style={{width:"10%"}}/>
            <col style={{width:"10%"}}/>
            <col style={{width:"12%"}}/>
          </colgroup>
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Company</th><th>Role</th><th>Joined</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {users.loading && <tr><td colSpan={7} style={{padding:30, textAlign:"center", color:"var(--text-mute)"}}>Loading…</td></tr>}
            {!users.loading && rows.length === 0 && (
              <tr><td colSpan={7} style={{padding:30, textAlign:"center", color:"var(--text-mute)"}}>No users match these filters.</td></tr>
            )}
            {rows.map(u=>(
              <tr key={u.id}>
                <td title={u.name || ""} style={{overflow:"hidden", textOverflow:"ellipsis"}}>
                  <div className="row" style={{gap:10, minWidth:0}}>
                    <div className="user-avatar" style={{width:28, height:28, fontSize:11, flexShrink:0}}>
                      {(u.name || u.email).split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                    </div>
                    <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{u.name}</span>
                  </div>
                </td>
                <td className="dim" title={u.email || ""} style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{u.email}</td>
                <td title={u.company_name || ""} style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{u.company_name || "—"}</td>
                <td className="dim" title={u.user_title || u.account_type || ""} style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{u.user_title || u.account_type || "—"}</td>
                <td className="dim" style={{whiteSpace:"nowrap"}}>{u.joined ? new Date(u.joined).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "—"}</td>
                <td><StatusPill status={statusOf(u)}/></td>
                <td>
                  <div className="row" style={{gap:4, justifyContent:"flex-end", flexWrap:"wrap"}}>
                    {meIsSuperuser && u.email !== me.data?.email && !u.is_suspended && (
                      <button
                        className="btn btn--quiet"
                        style={{padding:"4px 8px", fontSize:11}}
                        onClick={() => startViewAs(u)}
                        title={`See the app as ${u.name || u.email} would.`}
                      >View as</button>
                    )}
                    {u.is_suspended ? (
                      <button className="btn btn--quiet" style={{padding:"4px 8px", fontSize:11}} onClick={() => callAction(u, "reactivate")}>Reactivate</button>
                    ) : (
                      <button className="btn btn--quiet" style={{padding:"4px 8px", fontSize:11}} onClick={() => callAction(u, "suspend")}>Suspend</button>
                    )}
                    {u.account_type === "administrator" && !u.is_admin_approved && (
                      <button className="btn btn--primary" style={{padding:"4px 8px", fontSize:11}} onClick={() => callAction(u, "approve-admin")}>Approve</button>
                    )}
                    {u.account_type === "investor" && !u.is_investor_approved && (
                      <button className="btn btn--primary" style={{padding:"4px 8px", fontSize:11}} onClick={() => callAction(u, "approve-investor")}>Approve</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminAnalytics() {
  const analytics = useResource("/admin/analytics/");
  const kpis = analytics.data?.kpis || {};
  const modalities = analytics.data?.modalities || [];

  return (
    <div className="page route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>Administration · Analytics</div>
          <h1 className="page-title">What the fund is <em>actually</em> evaluating.</h1>
        </div>
      </div>

      {analytics.error && (
        <div className="card" style={{padding:20, borderColor:"var(--bad)", color:"var(--bad)"}}>
          Couldn't load analytics. <button className="btn btn--ghost" style={{marginLeft:10}} onClick={analytics.reload}>Retry</button>
        </div>
      )}

      <div className="grid grid--4">
        <KPICard label="Decks · YTD" value={(kpis.decks_ytd ?? 0).toLocaleString()}/>
        <KPICard label="Decks · 30d" value={(kpis.decks_30d ?? 0).toLocaleString()}/>
        <KPICard label="Users · total" value={(kpis.users_total ?? 0).toLocaleString()}/>
        <KPICard label="Users · active" value={(kpis.users_active ?? 0).toLocaleString()}/>
      </div>

      <div className="grid" style={{gridTemplateColumns:"2fr 1fr", gap:24, marginTop:28}}>
        <div className="card">
          <div className="h-eyebrow">Analysis throughput</div>
          <p className="dim" style={{fontSize:13, marginTop:8}}>
            {kpis.decks_30d ?? 0} in the last 30 days, {kpis.decks_ytd ?? 0} year-to-date.
          </p>
          <div style={{height:220, marginTop:12}}>
            <Spark data={Array.from({length:90},(_,i)=>60+Math.sin(i/8)*20+i/3)} fill="var(--accent-soft)"/>
          </div>
          <div className="as-of" style={{marginTop:6}}>Shape is illustrative — per-day history lands in Phase 6.</div>
        </div>
        <div className="card">
          <div className="h-eyebrow">Top modalities</div>
          <div style={{marginTop:14}}>
            {modalities.length === 0 && !analytics.loading && (
              <div className="dim" style={{textAlign:"center", padding:20}}>No analyses yet.</div>
            )}
            {modalities.map(({ name, count })=>(
              <div key={name} className="row" style={{padding:"8px 0", borderBottom:"1px solid var(--line)"}}>
                <span style={{fontSize:13}}>{name}</span>
                <div className="spacer"/>
                <span className="num">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = React.useState(() => currentAppearance("theme", "light"));
  const set = (t) => { setAppearance("theme", t); setTheme(t); };
  return (
    <div className="seg">
      <button className={theme === "light" ? "on" : ""} onClick={() => set("light")}>Light</button>
      <button className={theme === "dark" ? "on" : ""} onClick={() => set("dark")}>Dark</button>
    </div>
  );
}

const ADMIN_SETTINGS_TABS = [
  { id: "appearance",      label: "Appearance" },
  { id: "role-testing",    label: "Role testing" },
  { id: "prompt-sets",     label: "Prompt sets" },
  { id: "llm-providers",   label: "LLM providers" },
  { id: "scoring-weights", label: "Scoring weights" },
  { id: "market-research", label: "Market research" },
  { id: "sso-security",    label: "SSO & security" },
  { id: "email",           label: "Email" },
  { id: "billing",         label: "Billing" },
];

function StatusPillSet({ ok, okLabel, badLabel }) {
  return ok
    ? <span className="pill pill--good"><i className="dot"/>{okLabel || "Configured"}</span>
    : <span className="pill pill--bad"><i className="dot"/>{badLabel || "Missing"}</span>;
}

function FieldRow({ label, children }) {
  return (
    <div className="row" style={{padding:"10px 0", borderTop:"1px solid var(--line)", alignItems:"center", gap:12}}>
      <span style={{width:170, fontSize:13, color:"var(--text-mute)"}}>{label}</span>
      <div style={{flex:1, fontSize:13}}>{children}</div>
    </div>
  );
}

function bytesPretty(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function AdminSettings() {
  const [activeTab, setActiveTab] = React.useState(() => {
    try {
      const saved = localStorage.getItem("vz-admin-settings-tab");
      if (saved && ADMIN_SETTINGS_TABS.some(t => t.id === saved)) return saved;
    } catch { /* ignore */ }
    return ADMIN_SETTINGS_TABS[0].id;
  });

  const selectTab = (id) => {
    setActiveTab(id);
    try { localStorage.setItem("vz-admin-settings-tab", id); } catch { /* ignore */ }
  };

  const status = useResource("/admin/system-status/");
  const sys = status.data || {};

  return (
    <div className="page route-enter">
      <div className="page-header">
        <div>
          <div className="h-eyebrow" style={{marginBottom:8}}>Administration · Settings</div>
          <h1 className="page-title">System configuration.</h1>
        </div>
      </div>

      <div className="grid" style={{gridTemplateColumns:"240px 1fr", gap:32, alignItems:"start"}}>
        <div className="stack">
          {ADMIN_SETTINGS_TABS.map(t => (
            <div
              key={t.id}
              role="button"
              tabIndex={0}
              className={"nav-item" + (activeTab === t.id ? " active" : "")}
              onClick={() => selectTab(t.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectTab(t.id); } }}
              style={{cursor:"pointer"}}
            >
              {t.label}
            </div>
          ))}
        </div>

        <div className="stack">
          {status.loading && (
            <div className="card"><div className="dim" style={{fontSize:13}}>Loading current configuration…</div></div>
          )}
          {status.error && (
            <div className="card"><div className="dim" style={{fontSize:13, color:"var(--bad)"}}>Couldn't load system status: {status.error.message || "unknown error"}</div></div>
          )}

          {activeTab === "appearance"      && <AppearancePanel/>}
          {activeTab === "role-testing"    && <RoleTestingPanel/>}
          {activeTab === "prompt-sets"     && <PromptSetsPanel data={sys.prompt_sets} reload={status.reload}/>}
          {activeTab === "llm-providers"   && <LLMProvidersPanel data={sys.providers} reload={status.reload}/>}
          {activeTab === "scoring-weights" && <ScoringWeightsPanel/>}
          {activeTab === "market-research" && <MarketResearchPanel data={sys.research}/>}
          {activeTab === "sso-security"    && <SSOSecurityPanel data={sys.security}/>}
          {activeTab === "email"           && <EmailPanel data={sys.email}/>}
          {activeTab === "billing"         && <BillingPanel data={sys.billing}/>}
        </div>
      </div>
    </div>
  );
}

// --- Tab panels --------------------------------------------------------

function AppearancePanel() {
  const [palette, setPalette] = React.useState(() => currentAppearance("palette", "indigo"));
  const palettes = [
    { id: "indigo",    label: "Indigo" },
    { id: "ultramarine", label: "Ultramarine" },
    { id: "emerald",   label: "Emerald" },
    { id: "amber",     label: "Amber" },
    { id: "rose",      label: "Rose" },
    { id: "graphite",  label: "Graphite" },
  ];
  const setP = (v) => { setAppearance("palette", v); setPalette(v); };
  return (
    <>
      <div className="card">
        <div className="h-eyebrow">Appearance</div>
        <h3 className="h-display" style={{fontSize:"var(--text-2xl)", margin:"4px 0 6px"}}>Theme</h3>
        <p className="dim" style={{fontSize:13, marginBottom:16}}>
          Light by default. If your device is set to dark mode, we match it until you choose here.
        </p>
        <div className="row" style={{gap:16, alignItems:"center"}}>
          <ThemeToggle/>
          <span className="as-of">Saved to this browser</span>
        </div>
      </div>
      <div className="card">
        <div className="h-eyebrow">Palette</div>
        <p className="dim" style={{fontSize:13, marginTop:6, marginBottom:14}}>
          Accent colour for buttons, charts, and active states across the app.
        </p>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))", gap:8}}>
          {palettes.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => setP(p.id)}
              className={"btn " + (palette === p.id ? "btn--primary" : "btn--ghost")}
              style={{justifyContent:"center"}}
            >{p.label}</button>
          ))}
        </div>
      </div>
    </>
  );
}

function PromptSetsPanel({ data, reload }) {
  const files = data?.files || [];
  const active = data?.active || "";
  const fileInputRef = React.useRef(null);
  const [picked, setPicked] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [notice, setNotice] = React.useState(null);
  const [error, setError] = React.useState(null);

  function onPick(e) {
    const f = e.target.files?.[0] || null;
    setPicked(f);
    setNotice(null);
    setError(null);
  }

  async function upload() {
    if (!picked) return;
    setUploading(true);
    setError(null);
    setNotice(null);
    try {
      const fd = new FormData();
      fd.append("file", picked);
      const resp = await apiPost("/admin/prompt-sets/", fd);
      setNotice(`Uploaded ${resp.name}${resp.stored_in_s3 ? " · synced to S3" : " · stored locally"} · now active.`);
      setPicked(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      reload && reload();
    } catch (e) {
      setError(e.body?.error || e.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="card">
      <div className="h-eyebrow">Prompt sets</div>
      <h3 className="h-display" style={{fontSize:"var(--text-2xl)", margin:"4px 0 6px"}}>{active || "—"}</h3>
      <p className="dim" style={{fontSize:13, marginBottom:14}}>
        CSV files that drive the analyzer's prompt templates. The most recently uploaded file becomes the live database automatically.
      </p>
      <div style={{borderTop:"1px solid var(--line)"}}>
        {files.length === 0 && (
          <div className="dim" style={{fontSize:13, padding:"14px 0"}}>No prompt sets uploaded yet.</div>
        )}
        {files.map(f => (
          <div key={f.id ?? f.name} className="row" style={{padding:"10px 0", borderBottom:"1px solid var(--line)", alignItems:"center"}}>
            <span style={{flex:1, fontSize:13, fontFamily:"var(--ff-mono)", overflow:"hidden", textOverflow:"ellipsis"}}>{f.name}</span>
            <span className="num dim" style={{width:90, textAlign:"right", fontSize:12}}>{bytesPretty(f.size_bytes)}</span>
            <span className="dim" style={{width:120, textAlign:"right", fontSize:12}}>
              {f.created_at ? new Date(f.created_at).toLocaleDateString() : "—"}
            </span>
            {active === f.name
              ? <span className="pill pill--good" style={{marginLeft:12}}><i className="dot"/>Active</span>
              : <span style={{marginLeft:12, width:64}}/>}
          </div>
        ))}
      </div>

      {notice && (
        <div style={{marginTop:14, padding:"10px 12px", border:"1px solid var(--accent-hi)", background:"var(--accent-soft)", borderRadius:4, fontSize:13}}>{notice}</div>
      )}
      {error && (
        <div style={{marginTop:14, padding:"10px 12px", border:"1px solid var(--bad)", color:"var(--bad)", background:"color-mix(in srgb, var(--bad) 10%, transparent)", borderRadius:4, fontSize:13}}>{error}</div>
      )}

      <div style={{marginTop:18, paddingTop:14, borderTop:"1px solid var(--line)"}}>
        <div className="h-eyebrow" style={{marginBottom:8}}>Update prompt database</div>
        <p className="dim" style={{fontSize:12, marginBottom:12, maxWidth:520}}>
          Upload a new <code>.csv</code> with the prompt definitions. It becomes the active set immediately and is mirrored to S3 if AWS credentials are configured.
        </p>
        <div className="row" style={{gap:10, alignItems:"center", flexWrap:"wrap"}}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onPick}
            style={{
              fontSize:13,
              fontFamily:"inherit",
              padding:"6px 8px",
              border:"1px solid var(--line-strong)",
              borderRadius:4,
              background:"var(--surface)",
              color:"var(--text)",
            }}
          />
          {picked && (
            <span className="dim" style={{fontSize:12}}>
              {picked.name} · {bytesPretty(picked.size)}
            </span>
          )}
          <div className="spacer"/>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!picked || uploading}
            onClick={upload}
            style={{opacity: !picked || uploading ? 0.6 : 1}}
          >
            {uploading ? "Uploading…" : "Update Prompt Database"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LLMProvidersPanel({ data, reload }) {
  const [draft, setDraft] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (data && draft == null) {
      setDraft({
        llm_provider: data.llm_provider || "anthropic",
        llm_model: data.llm_model || "",
        llm_temperature: data.llm_temperature ?? 0,
        llm_timeout: data.llm_timeout ?? 60,
      });
    }
  }, [data]);

  if (!data) return null;
  if (!draft) return <div className="card"><div className="dim" style={{fontSize:13}}>Loading…</div></div>;

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await apiPatch("/admin/settings/", draft);
      setNotice("Provider settings saved.");
      reload && reload();
    } catch (e) {
      setError(e.body?.error || e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  const dirty =
    draft.llm_provider !== data.llm_provider ||
    (draft.llm_model || "") !== (data.llm_model || "") ||
    Number(draft.llm_temperature) !== Number(data.llm_temperature) ||
    Number(draft.llm_timeout) !== Number(data.llm_timeout);

  return (
    <>
      <div className="card">
        <div className="h-eyebrow">API keys</div>
        <FieldRow label="Anthropic">
          <StatusPillSet ok={data.anthropic_key_set} okLabel="ANTHROPIC_API_KEY set" badLabel="Not configured"/>
        </FieldRow>
        <FieldRow label="OpenAI">
          <StatusPillSet ok={data.openai_key_set} okLabel="OPENAI_API_KEY set" badLabel="Not configured"/>
        </FieldRow>
        <p className="dim" style={{fontSize:12, marginTop:14}}>
          Keys live in the server's environment (Render Dashboard in production, <code>.env</code> locally) — they're never written from this UI.
        </p>
      </div>

      <div className="card">
        <div className="h-eyebrow">Active provider</div>
        <h3 className="h-display" style={{fontSize:"var(--text-2xl)", margin:"4px 0 14px"}}>Routing</h3>
        {notice && <div style={{marginBottom:12, padding:"10px 12px", border:"1px solid var(--accent-hi)", background:"var(--accent-soft)", borderRadius:4, fontSize:13}}>{notice}</div>}
        {error && <div style={{marginBottom:12, padding:"10px 12px", border:"1px solid var(--bad)", color:"var(--bad)", background:"color-mix(in srgb, var(--bad) 10%, transparent)", borderRadius:4, fontSize:13}}>{error}</div>}

        <FieldRow label="Provider">
          <select
            value={draft.llm_provider}
            onChange={(e) => set("llm_provider", e.target.value)}
            style={{padding:"8px 10px", border:"1px solid var(--line-strong)", borderRadius:4, background:"var(--surface)", fontSize:13}}
          >
            <option value="anthropic">Anthropic</option>
            <option value="openai">OpenAI</option>
          </select>
        </FieldRow>
        <FieldRow label="Model override">
          <input
            type="text"
            value={draft.llm_model}
            placeholder="leave blank for provider default"
            onChange={(e) => set("llm_model", e.target.value)}
            style={{padding:"8px 10px", border:"1px solid var(--line-strong)", borderRadius:4, background:"var(--surface)", fontSize:13, fontFamily:"var(--ff-mono)", width:"100%", maxWidth:320}}
          />
        </FieldRow>
        <FieldRow label="Temperature">
          <input
            type="number" min="0" max="2" step="0.1"
            value={draft.llm_temperature}
            onChange={(e) => set("llm_temperature", e.target.value)}
            style={{padding:"8px 10px", border:"1px solid var(--line-strong)", borderRadius:4, background:"var(--surface)", fontSize:13, width:120}}
          />
          <span className="dim" style={{fontSize:12, marginLeft:8}}>0 = deterministic</span>
        </FieldRow>
        <FieldRow label="Timeout (seconds)">
          <input
            type="number" min="5" max="600" step="5"
            value={draft.llm_timeout}
            onChange={(e) => set("llm_timeout", e.target.value)}
            style={{padding:"8px 10px", border:"1px solid var(--line-strong)", borderRadius:4, background:"var(--surface)", fontSize:13, width:120}}
          />
        </FieldRow>

        <div className="row" style={{marginTop:16, gap:8}}>
          <button className="btn btn--primary" disabled={!dirty || saving} onClick={save} style={{opacity: dirty && !saving ? 1 : 0.6}}>
            {saving ? "Saving…" : "Save provider settings"}
          </button>
          {data.updated_at && (
            <span className="as-of">Last updated {new Date(data.updated_at).toLocaleString()}{data.updated_by ? ` · ${data.updated_by}` : ""}</span>
          )}
        </div>
      </div>
    </>
  );
}

function ScoringWeightsPanel() {
  return (
    <div className="card">
      <div className="h-eyebrow">Scoring weights</div>
      <p className="dim" style={{fontSize:13, marginTop:8, marginBottom:16}}>
        Current IAS composition. Editing weights is a Phase 4 feature — these display the canonical defaults shipped with the analyzer.
      </p>
      {MOCK.iasBreakdown.map(b => (
        <div key={b.key} className="row" style={{padding:"10px 0", borderTop:"1px solid var(--line)"}}>
          <span style={{width:130, fontSize:14}}>{b.key}</span>
          <div style={{flex:1, height:3, background:"var(--line)", borderRadius:2, overflow:"hidden"}}>
            <div style={{width:(b.weight*4*100)+"%", height:"100%", background:"var(--accent-hi)"}}/>
          </div>
          <span className="num dim" style={{width:60, textAlign:"right"}}>{(b.weight*100).toFixed(0)}%</span>
        </div>
      ))}
    </div>
  );
}

function MarketResearchPanel({ data }) {
  if (!data) return null;
  return (
    <div className="card">
      <div className="h-eyebrow">Market research</div>
      <h3 className="h-display" style={{fontSize:"var(--text-2xl)", margin:"4px 0 6px"}}>Powered by Anthropic</h3>
      <p className="dim" style={{fontSize:13, marginBottom:14}}>
        Market sizing, competitor maps, and citation pulls run through Claude using the same Anthropic key as the deck analyzer.
        No third-party search vendor (Tavily, Perplexity, SerpAPI) is wired up — add one and we'll surface it here.
      </p>
      <FieldRow label="Anthropic key">
        <StatusPillSet ok={data.anthropic_key_set} okLabel="ANTHROPIC_API_KEY set" badLabel="Not configured"/>
      </FieldRow>
      <FieldRow label="Mode">
        <span className="pill"><i className="dot"/>LLM-only</span>
      </FieldRow>
    </div>
  );
}

function SSOSecurityPanel({ data }) {
  if (!data) return null;
  return (
    <>
      <div className="card">
        <div className="h-eyebrow">Bot protection</div>
        <FieldRow label="reCAPTCHA">
          <StatusPillSet ok={data.recaptcha_enabled} okLabel="Enabled" badLabel="Disabled"/>
        </FieldRow>
        <FieldRow label="Site key">
          <StatusPillSet ok={data.recaptcha_site_key_set} okLabel="Set" badLabel="Missing"/>
        </FieldRow>
        <FieldRow label="Secret key">
          <StatusPillSet ok={data.recaptcha_secret_key_set} okLabel="Set" badLabel="Missing"/>
        </FieldRow>
      </div>
      <div className="card">
        <div className="h-eyebrow">Session & CSRF</div>
        <FieldRow label="DEBUG mode">
          {data.debug
            ? <span className="pill pill--bad"><i className="dot"/>On (development)</span>
            : <span className="pill pill--good"><i className="dot"/>Off (production-safe)</span>}
        </FieldRow>
        <FieldRow label="CSRF in session">
          <StatusPillSet ok={data.csrf_use_sessions} okLabel="Yes" badLabel="No"/>
        </FieldRow>
        <FieldRow label="CSRF cookie HttpOnly">
          <StatusPillSet ok={data.csrf_cookie_httponly} okLabel="Yes" badLabel="No"/>
        </FieldRow>
        <FieldRow label="Session cookie age">
          <span>{Math.round((data.session_cookie_age_seconds || 0) / 3600)} hours</span>
        </FieldRow>
        <FieldRow label="Allowed hosts">
          <code style={{fontSize:12}}>{(data.allowed_hosts || []).join(", ") || "—"}</code>
        </FieldRow>
      </div>
      <div className="card">
        <div className="h-eyebrow">SSO</div>
        <p className="dim" style={{fontSize:13, marginTop:8}}>
          Single sign-on (Google Workspace, Okta, Azure AD) isn't wired up yet. Available on request as part of the enterprise package.
        </p>
      </div>
    </>
  );
}

function EmailPanel({ data }) {
  const [sending, setSending] = React.useState(false);
  const [notice, setNotice] = React.useState(null);
  const [error, setError] = React.useState(null);

  if (!data) return null;

  async function sendTest() {
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const resp = await apiPost("/admin/send-test-email/", {});
      const where = resp?.backend?.includes("smtp") ? "your inbox" : "the dev console";
      setNotice(`Test email sent to ${resp.sent_to} via ${resp.backend.split('.').slice(-2, -1)[0]}. Check ${where}.`);
    } catch (e) {
      setError(e.body?.error || e.message || "Send failed.");
    } finally {
      setSending(false);
    }
  }

  const isConsole = (data.backend || "").endsWith(".console.EmailBackend");

  return (
    <>
      <div className="card">
        <div className="h-eyebrow">Backend</div>
        <FieldRow label="Mode">
          {data.is_smtp
            ? <span className="pill pill--good"><i className="dot"/>SMTP</span>
            : isConsole
              ? <span className="pill"><i className="dot"/>Console (logs only)</span>
              : <span className="pill"><i className="dot"/>{data.backend || "Unknown"}</span>}
        </FieldRow>
        <FieldRow label="Backend class"><code style={{fontSize:12}}>{data.backend || "—"}</code></FieldRow>
      </div>

      <div className="card">
        <div className="h-eyebrow">SMTP</div>
        <FieldRow label="Host"><code style={{fontSize:12}}>{data.host || "—"}</code></FieldRow>
        <FieldRow label="Port">{data.port ?? "—"}</FieldRow>
        <FieldRow label="TLS"><StatusPillSet ok={data.use_tls} okLabel="On" badLabel="Off"/></FieldRow>
        <FieldRow label="SSL"><StatusPillSet ok={data.use_ssl} okLabel="On" badLabel="Off"/></FieldRow>
        <FieldRow label="Username"><code style={{fontSize:12}}>{data.host_user || "—"}</code></FieldRow>
        <FieldRow label="Password"><StatusPillSet ok={data.host_password_set} okLabel="Set" badLabel="Empty"/></FieldRow>
        <FieldRow label="From"><code style={{fontSize:12}}>{data.from_email || "—"}</code></FieldRow>
      </div>

      <div className="card">
        <div className="h-eyebrow">Test</div>
        <p className="dim" style={{fontSize:13, marginTop:8, marginBottom:14}}>
          Send a small test email to your own admin address. {isConsole && "In console mode, the message body is printed to the dev server's terminal."}
        </p>
        {notice && <div style={{marginBottom:12, padding:"10px 12px", border:"1px solid var(--accent-hi)", background:"var(--accent-soft)", borderRadius:4, fontSize:13}}>{notice}</div>}
        {error && <div style={{marginBottom:12, padding:"10px 12px", border:"1px solid var(--bad)", color:"var(--bad)", background:"color-mix(in srgb, var(--bad) 10%, transparent)", borderRadius:4, fontSize:13}}>{error}</div>}
        <button className="btn btn--ghost" disabled={sending} onClick={sendTest} style={{opacity: sending ? 0.6 : 1}}>
          {sending ? "Sending…" : "Send test email"}
        </button>
      </div>
    </>
  );
}

function RoleTestingPanel() {
  const me = useMe();
  const [busy, setBusy] = React.useState(null); // null | "company" | "investor" | "stop"
  const [error, setError] = React.useState(null);
  const viewing = me.data?.view_as?.active;

  async function viewAsRole(role) {
    setBusy(role);
    setError(null);
    try {
      await apiPost("/admin/view-as/", { role });
      try {
        localStorage.removeItem("vz-route");
        localStorage.removeItem("vz-analysis-id");
        localStorage.removeItem("vz-submission-id");
      } catch { /* ignore */ }
      location.href = "/";
    } catch (e) {
      setError(e.body?.error || e.message || "Couldn't switch role.");
      setBusy(null);
    }
  }

  async function stop() {
    setBusy("stop");
    setError(null);
    try {
      await apiDelete("/admin/view-as/");
      try {
        localStorage.removeItem("vz-route");
        localStorage.removeItem("vz-analysis-id");
        localStorage.removeItem("vz-submission-id");
      } catch { /* ignore */ }
      location.href = "/";
    } catch (e) {
      setError(e.body?.error || e.message || "Couldn't return to admin.");
      setBusy(null);
    }
  }

  return (
    <>
      <div className="card">
        <div className="h-eyebrow">Role testing</div>
        <h3 className="h-display" style={{fontSize:"var(--text-2xl)", margin:"4px 0 6px"}}>See the app as someone else.</h3>
        <p className="dim" style={{fontSize:13, marginBottom:14, maxWidth:560}}>
          Click a role below to render the entire SPA as a representative user of that type would see it.
          Their sidebar items, dashboard, and submissions show through; permission-gated admin endpoints
          stop working until you return. A persistent banner at the top will let you switch back at any time.
        </p>

        {error && (
          <div style={{marginBottom:14, padding:"10px 12px", border:"1px solid var(--bad)", color:"var(--bad)", background:"color-mix(in srgb, var(--bad) 10%, transparent)", borderRadius:4, fontSize:13}}>{error}</div>
        )}

        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:6}}>
          <RoleCard
            title="Founder view"
            subtitle="What a company / startup user sees."
            target="charlieco@venzyme.life"
            disabled={!!busy || viewing}
            loading={busy === "company"}
            onClick={() => viewAsRole("company")}
          />
          <RoleCard
            title="Investor view"
            subtitle="What an approved investor sees."
            target="charlieinvestor@venzyme.life"
            disabled={!!busy || viewing}
            loading={busy === "investor"}
            onClick={() => viewAsRole("investor")}
          />
        </div>
      </div>

      {viewing && (
        <div className="card" style={{borderColor:"#f97316"}}>
          <div className="h-eyebrow" style={{color:"#f97316"}}>Currently impersonating</div>
          <p style={{fontSize:14, marginTop:6}}>
            You're seeing the app as <strong>{me.data?.name || me.data?.email}</strong>{" "}
            ({me.data?.account_type}). Real account: <strong>{me.data.view_as.real_user_email}</strong>.
          </p>
          <div className="row" style={{marginTop:14}}>
            <button className="btn btn--primary" disabled={busy === "stop"} onClick={stop}>
              {busy === "stop" ? "Returning…" : "Return to admin"}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="h-eyebrow">How this works</div>
        <p className="dim" style={{fontSize:13, marginTop:8, maxWidth:560}}>
          The "view as" toggle keeps your admin session intact on the server. A middleware swaps the
          effective user on each request only when you're an active superuser, so non-admins can't
          escalate this way. Admin-only API endpoints (suspend, approve, settings PATCH, etc.) return
          403 while you're impersonating, so you can't accidentally make destructive changes as the
          target user. Per-user impersonation is also available from Admin → Users for any active
          account.
        </p>
      </div>
    </>
  );
}

function RoleCard({ title, subtitle, target, disabled, loading, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: "left",
        background: "linear-gradient(180deg, var(--surface), var(--surface-2))",
        border: "1px solid var(--line-strong)",
        borderRadius: 6,
        padding: "16px 18px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.5 : 1,
        transition: "border-color var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease)",
        font: "inherit",
        color: "inherit",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = "var(--accent-hi)"; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.borderColor = "var(--line-strong)"; }}
    >
      <div className="h-eyebrow" style={{marginBottom:6}}>{title}</div>
      <div style={{fontSize:15, fontWeight:500, marginBottom:6}}>{loading ? "Switching…" : "Enter"}  →</div>
      <div className="dim" style={{fontSize:12, lineHeight:1.5}}>{subtitle}</div>
      <div style={{marginTop:10, fontSize:11, fontFamily:"var(--ff-mono)", color:"var(--text-mute)"}}>{target}</div>
    </button>
  );
}

function BillingPanel({ data }) {
  if (!data) return null;
  return (
    <div className="card">
      <div className="h-eyebrow">Plan</div>
      <h3 className="h-display" style={{fontSize:"var(--text-2xl)", margin:"4px 0 6px"}}>{data.plan || "—"}</h3>
      <p className="dim" style={{fontSize:13, marginBottom:14}}>
        Venzyme.LIFE is currently deployed as a self-hosted instance. There is no metered billing — usage is bounded by your provider quotas (Anthropic / OpenAI) and your Render plan.
      </p>
      <FieldRow label="Active users">
        <span className="num">{(data.seats_used ?? 0).toLocaleString()}</span>
      </FieldRow>
      <FieldRow label="Seat limit">
        <span>{data.seats_limit ?? "Unlimited"}</span>
      </FieldRow>
      <FieldRow label="Renews">
        <span>{data.renews_at || "—"}</span>
      </FieldRow>
    </div>
  );
}

